import { useEffect, useState } from "react";
import type {
  CompanyBusinessProfile,
  CompanyBusinessProfileInput
} from "@ai-sales/contracts";

const emptyProfile: CompanyBusinessProfileInput = {
  companyName: "",
  industry: "",
  subIndustry: "",
  productsAndServices: [],
  customerTypes: [],
  businessModels: [],
  businessTerms: [],
  departments: [],
  businessRoles: [],
  typicalBusinessProcesses: [],
  timeConsumingWork: [],
  businessRules: [],
  aiProhibitedActions: [],
  dataSensitivityNotes: ""
};

export function CompanyProfileForm(props: {
  profile: CompanyBusinessProfile | null;
  canManage: boolean;
  busy: boolean;
  onSave: (input: CompanyBusinessProfileInput) => Promise<void>;
}) {
  const [draft, setDraft] = useState<CompanyBusinessProfileInput>(() => profileInput(props.profile));
  useEffect(() => setDraft(profileInput(props.profile)), [props.profile]);
  const updateList = (
    field: keyof Pick<CompanyBusinessProfileInput,
    "productsAndServices" | "customerTypes" | "businessModels" | "businessTerms" | "departments" | "businessRoles" |
    "typicalBusinessProcesses" | "timeConsumingWork" | "businessRules" | "aiProhibitedActions">,
    value: string
  ) => setDraft((current) => ({
    ...current,
    [field]: value.split(/\n|，|,/).map((item) => item.trim()).filter(Boolean)
  }));
  const editable = props.canManage && !props.busy;
  const ready = draft.companyName.trim() && draft.industry.trim() &&
    draft.productsAndServices.length && draft.customerTypes.length && draft.businessModels.length;

  return <section className="admin-card">
    <div className="admin-card--header">
      <div>
        <h2>公司业务档案</h2>
        <p>使用业务语言描述公司和工作方式。这里不需要填写 Salesforce API 名称，后续场景可以复用。</p>
      </div>
      <span className="connection-pill">{props.profile ? `版本 ${props.profile.version}` : "尚未建立"}</span>
    </div>
    <div className="company-profile-grid">
      <label>公司名称<input value={draft.companyName} readOnly={!editable} onChange={(event) => setDraft({ ...draft, companyName: event.target.value })} placeholder="例如：某某科技有限公司" /></label>
      <label>所属行业<input value={draft.industry} readOnly={!editable} onChange={(event) => setDraft({ ...draft, industry: event.target.value })} placeholder="例如：企业软件、装备制造、专业服务" /></label>
      <label>细分领域<input value={draft.subIndustry} readOnly={!editable} onChange={(event) => setDraft({ ...draft, subIndustry: event.target.value })} placeholder="可选" /></label>
      <label>主要产品或服务<textarea rows={3} value={draft.productsAndServices.join("\n")} readOnly={!editable} onChange={(event) => updateList("productsAndServices", event.target.value)} placeholder="每行一项" /></label>
      <label>主要客户类型<textarea rows={3} value={draft.customerTypes.join("\n")} readOnly={!editable} onChange={(event) => updateList("customerTypes", event.target.value)} placeholder="例如：企业客户、渠道伙伴" /></label>
      <label>销售或服务模式<textarea rows={3} value={draft.businessModels.join("\n")} readOnly={!editable} onChange={(event) => updateList("businessModels", event.target.value)} placeholder="例如：直销、项目型销售、售后服务" /></label>
      <label>常用业务术语<textarea rows={3} value={draft.businessTerms.join("\n")} readOnly={!editable} onChange={(event) => updateList("businessTerms", event.target.value)} placeholder="每行一项，可选" /></label>
      <label>主要部门<textarea rows={3} value={draft.departments.join("\n")} readOnly={!editable} onChange={(event) => updateList("departments", event.target.value)} placeholder="每行一项，可选" /></label>
      <label>主要业务角色<textarea rows={3} value={draft.businessRoles.join("\n")} readOnly={!editable} onChange={(event) => updateList("businessRoles", event.target.value)} placeholder="每行一项，可选" /></label>
      <label>典型业务流程<textarea rows={3} value={draft.typicalBusinessProcesses.join("\n")} readOnly={!editable} onChange={(event) => updateList("typicalBusinessProcesses", event.target.value)} placeholder="例如：线索转商机、项目交付、售后问题闭环" /></label>
      <label>目前最耗时的工作<textarea rows={3} value={draft.timeConsumingWork.join("\n")} readOnly={!editable} onChange={(event) => updateList("timeConsumingWork", event.target.value)} placeholder="例如：整理拜访纪要、核对项目状态" /></label>
    </div>
    <label>重要业务规则<textarea rows={4} value={draft.businessRules.join("\n")} readOnly={!editable} onChange={(event) => updateList("businessRules", event.target.value)} placeholder="每行一条；例如：报价超过某金额必须审批" /></label>
    <label>AI 绝对不能做的事情<textarea rows={4} value={draft.aiProhibitedActions.join("\n")} readOnly={!editable} onChange={(event) => updateList("aiProhibitedActions", event.target.value)} placeholder="每行一条；例如：不得删除记录，不得自动批准折扣" /></label>
    <label>数据敏感性说明<textarea rows={3} value={draft.dataSensitivityNotes} readOnly={!editable} onChange={(event) => setDraft({ ...draft, dataSensitivityNotes: event.target.value })} placeholder="说明哪些数据不希望 AI 使用或展示" /></label>
    {props.canManage ? <div className="editor-actions">
      <button className="button button--primary" type="button" disabled={!ready || props.busy} onClick={() => void props.onSave(draft)}>
        {props.busy ? "保存中…" : props.profile ? "保存新版本" : "建立业务档案"}
      </button>
      <span className="muted">每次保存都会创建新版本，不覆盖历史档案。</span>
    </div> : null}
  </section>;
}

function profileInput(profile: CompanyBusinessProfile | null): CompanyBusinessProfileInput {
  if (!profile) return emptyProfile;
  const {
    companyName,
    industry,
    subIndustry,
    productsAndServices,
    customerTypes,
    businessModels,
    businessTerms,
    departments,
    businessRoles,
    typicalBusinessProcesses,
    timeConsumingWork,
    businessRules,
    aiProhibitedActions,
    dataSensitivityNotes
  } = profile;
  return {
    companyName,
    industry,
    subIndustry,
    productsAndServices,
    customerTypes,
    businessModels,
    businessTerms,
    departments,
    businessRoles,
    typicalBusinessProcesses,
    timeConsumingWork,
    businessRules,
    aiProhibitedActions,
    dataSensitivityNotes
  };
}
